// src/pages/About.jsx
import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import gradient from "../assets/gradient.png";
import ceoImg from "../assets/ceo.png";
import mdImg from "../assets/md.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay, ease: "easeOut" },
  }),
};

const About = () => {
  const { t } = useTranslation();

  return (
    <main
      dir={i18n.language === "ur" ? "rtl" : "ltr"}
      className="relative bg-black text-white overflow-hidden"
    >
      {/* SEO */}
      <Helmet>
        <html lang={i18n.language} />
        <title>{t("aboutPage.title")}</title>
        <meta name="description" content={t("aboutPage.description")} />
      </Helmet>

      {/* Background */}
      <img
        src={gradient}
        alt={t("aboutPage.heroHeading")}
        className="absolute inset-0 w-full h-full object-cover opacity-40 -z-10"
        loading="lazy"
      />

      {/* HERO */}
      <section className="relative text-center py-28 px-6 md:px-16">
        <motion.h1
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent mb-6"
        >
          {t("aboutPage.heroHeading")}
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          custom={0.2}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto text-gray-300 text-lg leading-relaxed"
        >
          {t("aboutPage.heroText")}
        </motion.p>
      </section>

      {/* WHO WE ARE */}
      <section className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center py-24 px-6 md:px-16 border-y border-blue-900/40">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1000&q=80"
            alt={t("aboutPage.whoWeAreHeading")}
            className="rounded-3xl shadow-2xl shadow-blue-900/30 object-cover w-full h-[420px]"
            loading="lazy"
          />
        </motion.div>

        <motion.div
          variants={fadeInUp}
          custom={0.3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-6">
            {t("aboutPage.whoWeAreHeading")}
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            {t("aboutPage.whoWeAreText1")}
          </p>
          <p className="text-gray-400 leading-relaxed">
            {t("aboutPage.whoWeAreText2")}
          </p>
        </motion.div>
      </section>

      {/* CEO MESSAGE */}
      <motion.section
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative max-w-4xl mx-auto text-center py-28 px-6"
      >
        <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-3xl p-10 shadow-lg hover:shadow-blue-900/40 transition-all duration-500">
          <img
           src={ceoImg}
            alt={t("aboutPage.ceoName")}
            className="w-32 h-32 mx-auto mb-6 rounded-full object-cover border-2 border-blue-500/60 shadow-lg"
            loading="lazy"
          />
          <h2 className="text-3xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-6">
            {t("aboutPage.ceoHeading")}
          </h2>
          <p className="text-gray-300 text-lg italic leading-relaxed mb-6">
            “{t("aboutPage.ceoText")}”
          </p>
          <h3 className="text-sky-400 font-semibold mt-4">
            {t("aboutPage.ceoName")}
          </h3>
        </div>
      </motion.section>

      {/* MANAGING DIRECTOR MESSAGE */}
      <motion.section
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative max-w-4xl mx-auto text-center py-24 px-6"
      >
        <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-3xl p-10 shadow-lg hover:shadow-blue-900/40 transition-all duration-500">
          <img
          src={mdImg}
            alt={t("aboutPage.mdName")}
            className="w-32 h-32 mx-auto mb-6 rounded-full object-cover border-2 border-blue-500/60 shadow-lg"
            loading="lazy"
          />
          <h2 className="text-3xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-6">
            {t("aboutPage.mdHeading")}
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {t("aboutPage.mdText1")}
          </p>
          <p className="text-sky-400 text-lg font-semibold italic mb-2">
            “{t("aboutPage.mdText2")}”
          </p>
          <h3 className="text-sky-400 font-semibold mt-2">
            {t("aboutPage.mdName")}
          </h3>
        </div>
      </motion.section>

      {/* OUR VISION */}
      <section className="max-w-7xl mx-auto py-28 px-6 md:px-16 text-center">
        <motion.h2
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-3xl font-semibold bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent mb-16"
        >
          {t("aboutPage.visionHeading")}
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-10">
          {t("aboutPage.visionPoints", { returnObjects: true }).map(
            (item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                custom={i * 0.2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="backdrop-blur-md bg-white/5 border border-gray-800/60 rounded-3xl p-8 shadow-md hover:shadow-blue-900/30 transition-all duration-300"
              >
                <img
                  src={item.img || "https://via.placeholder.com/600x400"}
                  alt={item.title}
                  className="rounded-2xl w-full h-44 object-cover mb-5"
                  loading="lazy"
                />
                <h3 className="text-xl font-semibold text-sky-400 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{item.text}</p>
              </motion.div>
            )
          )}
        </div>
      </section>
    </main>
  );
};

export default About;
